/**
 * IDs de célula (MongoDB ObjectId en cells._id).
 * No usar name, member_id ni otros campos como identificador de célula.
 */

const MONGO_OBJECT_ID_RE = /^[a-fA-F0-9]{24}$/;

export function isValidMongoObjectId(value) {
  const s = String(value ?? "").trim();
  return MONGO_OBJECT_ID_RE.test(s);
}

/**
 * @param {string|{ id?: string, _id?: string }|null|undefined} cellOrId
 * @returns {string} 24-char hex ObjectId o ""
 */
export function getCellMongoId(cellOrId) {
  if (cellOrId == null) return "";
  if (typeof cellOrId === "string") {
    const s = cellOrId.trim();
    return isValidMongoObjectId(s) ? s : "";
  }
  if (typeof cellOrId !== "object") return "";
  const candidates = [cellOrId.id, cellOrId._id];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (isValidMongoObjectId(s)) return s;
  }
  return "";
}

/** Normaliza respuesta API: siempre expone `id` y `_id` con el ObjectId real. */
export function normalizeCellFromApi(cell) {
  if (!cell || typeof cell !== "object") return cell;
  const id = getCellMongoId(cell);
  if (!id) return { ...cell };
  return { ...cell, id, _id: id };
}

/** Log temporal para depurar PUT/DELETE/GET de células (quitar cuando ya no haga falta). */
export function logCellApiDebug(action, payload = {}) {
  console.log("[ICJEME cell-api]", action, payload);
}

const CELL_ROLE_MEMBER_FIELDS = [
  "leader_id",
  "co_leader_id",
  "secretary_id",
  "treasurer_id",
  "host_id",
];

/** @param {string|{ id?: string, _id?: string }|null|undefined} memberOrId */
export function getMemberMongoId(memberOrId) {
  if (memberOrId == null) return "";
  if (typeof memberOrId === "string") {
    const s = memberOrId.trim();
    return isValidMongoObjectId(s) ? s : s;
  }
  if (typeof memberOrId !== "object") return "";
  const candidates = [memberOrId.id, memberOrId._id];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (isValidMongoObjectId(s)) return s;
    if (s) return s;
  }
  return "";
}

export function normalizeRefId(value) {
  return String(value ?? "").trim();
}

/** true si el miembro no tiene célula asignada (null, vacío o ausente). */
export function isMemberUnassignedCell(member) {
  if (!member) return true;
  return !normalizeRefId(member.cell_id);
}

/**
 * Miembro pertenece a la célula si cell_id coincide o tiene cargo en esa célula.
 * @param {{ id?: string, _id?: string, cell_id?: string, deleted_at?: unknown }} member
 * @param {string} filterCellId ObjectId de la célula seleccionada
 * @param {object|undefined} cellDoc documento de célula (para cargos)
 */
export function memberMatchesCellFilter(member, filterCellId, cellDoc) {
  const cid = getCellMongoId(filterCellId) || normalizeRefId(filterCellId);
  if (!cid) return true;
  if (member?.deleted_at) return false;
  if (isMemberUnassignedCell(member)) return false;
  if (normalizeRefId(member.cell_id) === cid) return true;
  const mid = getMemberMongoId(member);
  if (!mid || !cellDoc) return false;
  return CELL_ROLE_MEMBER_FIELDS.some((field) => normalizeRefId(cellDoc[field]) === mid);
}

const CELL_PARENT_REF_FIELDS = ["parent_cell_id", "parent_id", "enlace_id", "celula_referencia_id"];

const CHILD_LEADER_ROLE_LABELS = {
  leader: "Líder de célula hija",
  co_leader: "Co-líder de célula hija",
};

/** ID de la célula madre/enlace (solo por campos de referencia, no por nombre). */
export function getCellParentRefId(cell) {
  if (!cell || typeof cell !== "object") return "";
  for (const field of CELL_PARENT_REF_FIELDS) {
    const raw = cell[field];
    if (raw == null || raw === "") continue;
    const id = getCellMongoId(raw) || normalizeRefId(raw);
    if (id && isValidMongoObjectId(id)) return id;
  }
  return "";
}

/** True si `cell` es hija directa de `parentCellId` (un solo nivel, sin recursión). */
export function isDirectChildCell(cell, parentCellId) {
  const parentId = getCellMongoId(parentCellId) || normalizeRefId(parentCellId);
  if (!parentId || !cell) return false;
  if (cell.deleted_at) return false;
  const childParentId = getCellParentRefId(cell);
  return childParentId === parentId;
}

function resolveRoleMemberId(cell, field) {
  const raw = cell?.[field];
  if (raw == null || raw === "") return "";
  return getMemberMongoId(raw) || normalizeRefId(raw);
}

/**
 * Líderes y co-líderes de células hijas directas de una célula madre/enlace.
 * Sin recursión; sin secretario/tesorero/anfitrión; miembros únicos.
 *
 * @returns {Array<{ member: object, roleLabel: string }>}
 */
export function getDirectChildLeaderMembers(parentCellId, cells, members) {
  const parentId = getCellMongoId(parentCellId) || normalizeRefId(parentCellId);
  if (!parentId || !Array.isArray(cells) || !Array.isArray(members)) return [];

  const childCells = cells.filter((c) => isDirectChildCell(c, parentId));
  /** @type {Map<string, 'leader'|'co_leader'>} */
  const roleRank = new Map();

  for (const child of childCells) {
    const leaderId = resolveRoleMemberId(child, "leader_id");
    const coLeaderId = resolveRoleMemberId(child, "co_leader_id");
    if (leaderId) {
      const prev = roleRank.get(leaderId);
      if (!prev || prev === "co_leader") roleRank.set(leaderId, "leader");
    }
    if (coLeaderId && coLeaderId !== leaderId) {
      if (!roleRank.has(coLeaderId)) roleRank.set(coLeaderId, "co_leader");
    }
  }

  const activeMembers = members.filter((m) => !m.deleted_at);
  const byId = new Map();
  for (const m of activeMembers) {
    const mid = getMemberMongoId(m);
    if (mid) byId.set(mid, m);
  }

  const out = [];
  for (const [mid, role] of roleRank) {
    const member = byId.get(mid);
    if (!member) continue;
    out.push({
      member,
      roleLabel: CHILD_LEADER_ROLE_LABELS[role] || CHILD_LEADER_ROLE_LABELS.leader,
    });
  }

  out.sort((a, b) => String(a.member.name || "").localeCompare(String(b.member.name || ""), "es"));
  return out;
}
